import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRevisionEntries } from '../revisions/useRevisionEntries';
import { RevisionEntry } from '../revisions/revisionModel';
import { AssetViewerItem } from './viewerItems';

interface UseViewerRevisionsProps {
  accountId: number;
  catalogItemUuid: string;
  /** The item of the list on screen (the arrows move through these). */
  item: AssetViewerItem;
  /** The key the viewer opened on, and the revision of it to show first (`?asset=&rev=`). */
  startKey: string;
  startRevision: number | undefined;
  /** Opens the "Make a new revision" dialog of the start item once its history loads. */
  startWithNewRevision?: boolean;
}

/**
 * The revision state of the viewer (Releases v2, design d1–d4): the history of the item on screen,
 * the revision that the stage shows (the item itself until the user opens another one), the
 * revision of a removed file (d4), the Compare pair (d2) and the revision the dialog starts from (d3).
 * A move to another item shows that item again and closes Compare and the dialog.
 */
export function useViewerRevisions(props: UseViewerRevisionsProps) {
  const { accountId, catalogItemUuid, item, startKey, startRevision, startWithNewRevision = false } = props;
  const { entries, isError } = useRevisionEntries({ accountId, catalogItemUuid, item });
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [compareKeys, setCompareKeys] = useState<string[] | null>(null);
  const [dialogEntry, setDialogEntry] = useState<RevisionEntry | null>(null);
  const startDone = useRef(false);

  useEffect(() => {
    setOpenKey(null);
    setCompareKeys(null);
    setDialogEntry(null);
  }, [item.key]);

  // The deep link `?asset=<uuid>&rev=<n>` opens revision n of the start item once its history loads.
  useEffect(() => {
    if (startDone.current || startRevision === undefined || item.key !== startKey || entries.length === 0) {
      return;
    }
    startDone.current = true;
    const wanted = entries.find((entry) => entry.number === startRevision);
    if (wanted !== undefined && wanted.key !== item.key) {
      setOpenKey(wanted.key);
    }
  }, [entries, item.key, startKey, startRevision]);

  // "New revision" of a card opens the viewer with the dialog of that asset (design c1, d3).
  const dialogDone = useRef(false);
  useEffect(() => {
    if (dialogDone.current || !startWithNewRevision || item.key !== startKey || entries.length === 0) {
      return;
    }
    dialogDone.current = true;
    setDialogEntry(entries.find((entry) => entry.key === item.key) ?? entries[0] ?? null);
  }, [entries, item.key, startKey, startWithNewRevision]);

  const openEntry = useMemo(
    () => entries.find((entry) => entry.key === (openKey ?? item.key)) ?? null,
    [entries, item.key, openKey]
  );
  const shownItem = openKey === null ? item : (openEntry?.viewerItem ?? item);
  const goneEntry = openKey !== null && openEntry?.state === 'fileRemoved' ? openEntry : null;

  const open = useCallback((entry: RevisionEntry) => setOpenKey(entry.key === item.key ? null : entry.key), [item.key]);
  const closeCompare = useCallback(() => setCompareKeys(null), []);
  const closeDialog = useCallback(() => setDialogEntry(null), []);
  // "New revision" of the side panel starts from the revision on the stage.
  const startDialog = useCallback((entry?: RevisionEntry) => setDialogEntry(entry ?? openEntry ?? null), [openEntry]);

  return {
    entries,
    isError,
    /** The key of the revision on the stage. */
    stageKey: openEntry?.key ?? item.key,
    shownItem,
    goneEntry,
    open,
    compareKeys,
    compare: setCompareKeys,
    closeCompare,
    dialogEntry,
    startDialog,
    closeDialog,
  };
}
